import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import ApiService from '../service/ApiService';
import { useUser } from './UserContext';

const IntroModalContext = createContext(null);

export function IntroModalProvider({ children }) {
    const { user } = useUser();
    const [userSettings, setUserSettings] = useState(null);
    const [missedVersions, setMissedVersions] = useState([]);
    const [currentVersion, setCurrentVersion] = useState(null); // The latest version available in the system
    const [isInitialized, setIsInitialized] = useState(false);
    const [initializedForUserId, setInitializedForUserId] = useState(null);

    // Check if version A is greater than version B
    const isVersionGreater = (vA, vB) => {
        if (!vB) return true;
        if (!vA) return false;

        const partsA = vA.split('.').map(Number);
        const partsB = vB.split('.').map(Number);

        for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
            const partA = partsA[i] || 0;
            const partB = partsB[i] || 0;
            if (partA > partB) return true;
            if (partA < partB) return false;
        }
        return false;
    };

    // Initialize logic
    useEffect(() => {
        console.log('[IntroModal] useEffect triggered, user?.id:', user?.id, 'initializedForUserId:', initializedForUserId);

        const initialize = async () => {
            console.log('[IntroModal] initialize() started for user:', user?.id);
            try {
                // 1. Get User Settings
                const settingsRes = await ApiService.User.GetUserSettings();

                // 2. Fetch Changelog Index
                const response = await fetch('/changelogs/index.json');
                if (!response.ok) throw new Error('Failed to fetch changelog index');

                const allVersions = await response.json();

                // 3. Find latest version
                const sortedVersions = allVersions.sort((a, b) => isVersionGreater(a.version, b.version) ? 1 : -1);
                const latestVer = sortedVersions[sortedVersions.length - 1]?.version;
                setCurrentVersion(latestVer);

                // 4. Resolve settings — if no settings exist or lastSeenVersion is empty (new user), treat latest version as already seen
                const rawSettings = settingsRes.success ? settingsRes.data : null;
                const hasValidLastSeen = rawSettings && rawSettings.lastSeenVersion;
                console.log('[IntroModal] settingsRes:', settingsRes, 'rawSettings:', rawSettings, 'hasValidLastSeen:', hasValidLastSeen, 'latestVer:', latestVer);
                const settings = hasValidLastSeen ? rawSettings : { lastSeenVersion: latestVer };
                setUserSettings(settings);

                // 5. If new user (no valid lastSeenVersion), save the latest version to DB so they skip intro
                if (!hasValidLastSeen && latestVer) {
                    console.log('[IntroModal] New user detected, calling markAsSeen with:', latestVer);
                    try {
                        await ApiService.User.MarkVersionSeen(latestVer);
                        console.log('[IntroModal] MarkVersionSeen completed');
                    } catch (e) {
                        console.error('Auto-mark version seen error:', e);
                    }
                }

                // 6. Calculate missed versions (will be empty for new users since lastSeenVersion = latestVer)
                const missed = sortedVersions.filter(v =>
                    isVersionGreater(v.version, settings.lastSeenVersion)
                );

                setMissedVersions(missed);
            } catch (error) {
                console.error('IntroModalContext initialization error:', error);
            } finally {
                setIsInitialized(true);
                setInitializedForUserId(user?.id);
            }
        };

        initialize();
    }, [user?.id]);

    const markAsSeen = async (versionToMark) => {
        const version = versionToMark || currentVersion;
        if (!version) return;

        try {
            await ApiService.User.MarkVersionSeen(version);
            setUserSettings(prev => ({
                ...prev,
                lastSeenVersion: version
            }));
            setMissedVersions([]); // Clear missed versions
        } catch (e) {
            console.error('MarkVersionSeen error:', e);
        }
    };

    const value = {
        isLoading: !isInitialized,
        missedVersions,
        currentVersion,
        markAsSeen
    };

    return (
        <IntroModalContext.Provider value={value}>
            {children}
        </IntroModalContext.Provider>
    );
}

export function useIntroModal() {
    const context = useContext(IntroModalContext);
    if (!context) {
        throw new Error('useIntroModal must be used within IntroModalProvider');
    }
    return context;
}

export default IntroModalContext;
